import { TaskPagedFilter } from "../misc/pagedFilter";
import {
  ColleagueFeedbackTaskCommentDto,
  DOIAssigmentDto,
  DocumentApprovalDto,
  ITaskHeader,
  PeerReviewApprovalDto,
  PeerReviewTaskCommentDto,
  PeerReviewTaskDetailsDto,
  TaskDetailsCommentDto,
  TaskDetailsDOIDto,
  TaskLegalDto,
  TaskResult,
} from "../models/task.model";
import { IUser } from "../models/user.model";
import ServiceBase from "./base.service";

export class TasksService extends ServiceBase {
  getMyTasks = async (pagedFilter: TaskPagedFilter): Promise<TaskResult> =>
    this.get(
      `tasks?pageNumber=${pagedFilter.pageNumber}&pageSize=${pagedFilter.pageSize}&sortField=${pagedFilter.sortField}&sortDescending=${pagedFilter.sortDescending}&showCompleted=${pagedFilter.showCompleted}`
    );

  header = async (taskId: string): Promise<ITaskHeader> =>
    this.get(`tasks/${taskId}`);

  teamMembers = async (taskId: number): Promise<IUser[]> =>
    this.get(`tasks/${taskId}/team-members`);

  assignTo = async (taskId: number, userId: number): Promise<void> =>
    await this.post(`tasks/${taskId}/assign/${userId}`);

  legalDetails = async (taskId: number): Promise<TaskDetailsCommentDto> =>
    this.get(`tasks/${taskId}/legal`);

  legal = async (
    dto: TaskLegalDto,
    uploadDocument?: File | null
  ): Promise<void> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    await this.postFile(`tasks/${dto.taskId}/legal`, formData);
  };

  getApproveReviewers = async (
    taskId: number
  ): Promise<PeerReviewApprovalDto> =>
    this.get(`tasks/${taskId}/approve-reviewers`);

  approveReviewers = async (dto: PeerReviewApprovalDto): Promise<void> => {
    return await this.post(`tasks/${dto.taskId}/approve-reviewers`, dto);
  };

  peerReviewDetails = async (
    taskId: number
  ): Promise<PeerReviewTaskDetailsDto> =>
    this.get(`tasks/${taskId}/peer-review`);

  peerReview = async (
    dto: PeerReviewTaskCommentDto,
    uploadDocument?: File | null
  ): Promise<void> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    await this.postFile(`tasks/peer-review`, formData);
  };

  colleagueFeedbackDetails = async (
    taskId: number
  ): Promise<TaskDetailsCommentDto> =>
    this.get(`tasks/${taskId}/colleague-feedback`);

  colleagueFeedback = async (
    dto: ColleagueFeedbackTaskCommentDto,
    uploadDocument?: File | null
  ): Promise<void> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    await this.postFile(`tasks/colleague-feedback`, formData);
  };

  doiAssignationDetails = async (taskId: number): Promise<TaskDetailsDOIDto> =>
    this.get(`tasks/${taskId}/doi-assignation`);

  doiAssignation = async (dto: DOIAssigmentDto): Promise<void> => {
    await this.post(`tasks/${dto.taskId}/doi-assignation`, dto);
  };

  doiAssignationAssignToMe = async (taskId: Number): Promise<void> => {
    await this.post(`tasks/${taskId}/doi-assignation-assign-to-me`);
  };

  approvalDocument = async (
    dto: DocumentApprovalDto,
    isDraft: boolean,
    byAuthor?: boolean
  ): Promise<void> => {
    const queryString = byAuthor ? "?byAuthor=true" : "";
    await this.post(
      `tasks/${dto.taskId}/approval-${
        isDraft ? "draft" : "final-version"
      }${queryString}`,
      dto
    );
  };

  approvalDeleteDocument = async (dto: DocumentApprovalDto): Promise<void> => {
    await this.post(`tasks/${dto.taskId}/approval-delete`, dto);
  };

  cancelDelete = async (dto: DocumentApprovalDto): Promise<void> => {
    await this.post(`tasks/${dto.taskId}/cancel-delete`, dto);
  };

  cancelPublication = async (
    dto: DocumentApprovalDto,
    isDraft: boolean
  ): Promise<void> => {
    await this.post(
      `tasks/${dto.taskId}/cancel-publication?isDraft=${isDraft}`,
      dto
    );
  };
}

const tasksService = new TasksService();

export default tasksService;
